import { useRef, useState } from 'react';
import { useAuthStore } from '../../state/authStore';
import { IEditUserProfileRequest, IUser } from '@cuspar/ywt.ch.model';
import { Button, Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, Label, toast } from '@cuspar/ui';
import Avatar from 'boring-avatars';
import { useTranslation } from 'react-i18next';

const MAX_SIZE = 128;

async function resizeImageToBase64(file: File): Promise<string> {
  const imageDataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = imageDataUrl;
  });

  const { naturalWidth, naturalHeight } = image;
  if (!naturalWidth || !naturalHeight) return imageDataUrl;

  // Calculate new dimensions keeping aspect ratio, max dimension 128
  const scale = Math.min(1, MAX_SIZE / Math.max(naturalWidth, naturalHeight));
  const targetWidth = Math.round(naturalWidth * scale);
  const targetHeight = Math.round(naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) return imageDataUrl;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(image, 0, 0, targetWidth, targetHeight);
  return canvas.toDataURL('image/png');
}

export const Avartar = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { user, editUserProfile } = useAuthStore();
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const onPickImage = () => {
    fileInputRef.current?.click();
  };

  const onFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('account.selectValidImage'));
      return;
    }
    try {
      const base64 = await resizeImageToBase64(file);
      setIsSaving(true);
      await editUserProfile({ avatar: base64 } as IEditUserProfileRequest);
      toast.success(t('account.avatarUpdated'));
    } catch (err) {
      toast.error(t('account.avatarUpdateFailed'));
    } finally {
      setIsSaving(false);
      // reset the file input so selecting the same file again triggers change
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onRemoveImage = () => {
    setIsRemoving(true);
    editUserProfile({ removeAvatar: true } as IEditUserProfileRequest);
    setIsRemoving(false);
    toast.success(t('account.avatarRemoved'));
  };

  return (
    <Card className='w-full'>
      <CardHeader>
        <CardTitle className='text-xl'>{t('account.avatar')}</CardTitle>
        <CardDescription>{t('account.avatarDescription')}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className='flex items-center gap-4'>
          <AvartarDisplay user={user} size='large' />
          <div className='flex flex-col gap-2'>
            <div className='flex items-center gap-2'>
              <input ref={fileInputRef} type='file' accept='image/*' className='hidden' onChange={onFileChange} />
            </div>
          </div>
        </div>
      </CardContent>
      <CardFooter className='bg-muted/70 border-t py-2 flex justify-between'>
        <Label className='text-xs text-muted-foreground'>{t('account.avatarUploadHint')}</Label>
        <div className='flex gap-2'>
          {user?.avatar && (
            <Button loading={isRemoving} variant='outline' size='sm' onClick={onRemoveImage}>
              {t('account.removeAvatar')}
            </Button>
          )}
          <Button loading={isSaving} type='button' onClick={onPickImage} size='sm'>
            {t('account.uploadAvatar')}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
};

export const AvartarDisplay = ({ user, size = 'small' }: { user?: IUser; size?: 'small' | 'medium' | 'large' }) => {
  if (!user) return null;

  const sizeMap = {
    small: 22,
    medium: 28,
    large: 52,
  };

  if (!user.avatar) return <Avatar className='rounded' name={user.email} size={sizeMap[size]} colors={['#819A91', '#A7C1A8', '#D1D8BE', '#EEEFE0']} square variant='bauhaus' />;

  return <img src={user.avatar} alt='Avatar' style={{ width: sizeMap[size], height: sizeMap[size] }} className={`rounded object-cover`} />;
};
